import getLookups from '../../views/Config/lookups'
import { capitalCase } from 'change-case';

/**
 * Generates a mapper function to convert values into objects suitable for rendering in a CreatableSelect component.
 * This higher-order function checks if the specified form is supported by looking for it in the predefined entries.
 * If the form is supported, it returns a function that converts a given value to an object. If the value matches one
 * of the predefined entries for the form, it returns the predefined entry object. Otherwise, it constructs a new
 * object, ensuring the label is in capital case for aesthetic presentation in the select menu.
 * 
 * This approach is particularly useful for dynamically converting backend database values (e.g., from a "head_trauma"
 * column storing an array of strings like ["concussion", "hit", ...]) into user-friendly objects in the frontend, 
 * while ensuring that the form specification is validated once upfront.
 * 
 * @param {string} form The name of the form for which the mapper is being generated. This should match a key in the
 * predefined entries object. The existence of the form is checked upfront.
 * @returns {Function} A function that takes a single string value as its argument and returns an object with `label`
 * and `value` properties. For predefined values, it returns the corresponding object; for new values, it returns an
 * object with the label capitalized and the original value preserved.
 * @throws {Error} Throws an error if the specified form is not supported, indicating that it does not exist in the
 * predefined entries, thus ensuring the form's validity is checked only once during the mapper generation.
 */
export function generateSelectFormMapper(form) {
    const predifinedEntries = getLookups();
    if (!Object.keys(predifinedEntries).includes(form)) {
        throw new Error(`Form: ${form} is not supported`)
    }
    return (value) => {
        let predifinedEntry = predifinedEntries[form].find(en => en.value === value)
        return predifinedEntry ? predifinedEntry : { label: capitalCase(value), value: value }
    }
}
